<script lang="ts">
	import Button from "@components/common/Button.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Layout from "@components/layout/Layout.svelte";
	import { hugeHeadingParallax } from "@utils/hugeHeadingParallax.js";
	import { iconMap } from "@core/assets/icons/iconMap.js";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import { SolutionCategorization } from "@core/schema/SolutionCategorization.js";
	import { solutionCategorizationLongNameMap } from "@core/schema/names/solutionCategorizationNameMap.js";
	import { getServicesSolutionsDetailPath } from "@core/schema/paths/getServicesSolutionsDetailPath.js";
	import type { ServicesSolutionsProps } from "./ServicesSolutionsProps.js";

	const { solutions, tenant }: ServicesSolutionsProps = $props();

	let activeTab = $state<SolutionCategorization>(SolutionCategorization.Tag);
	const itemsToRender = $derived(
		solutions
			.filter(({ categorization }) => categorization === activeTab)
			.map((solution) => ({
				id: solution.id,
				name: solution.name,
				icon: solution.icon ? iconMap[solution.icon] : undefined,
				description: solution.description,
				href: getServicesSolutionsDetailPath(tenant, solution),
			})),
	);
</script>

<Layout>
	<section class="full-width mx-2 md:mx-8 mb-8 2k:mx-auto 2k:w-[116rem]">
		<h1 class="huge-heading text-cmcsecondary text-center md:text-left slow-down" use:hugeHeadingParallax>
			<span class="xl:hidden">
				<Formatted text="stroke[[Zrychlíme sklady, snížíme náklady]] a naučíme vás dělat logistiku správně" />
			</span>
			<span class="hidden xl:inline">
				<Formatted
					text="stroke[[Zrychlíme sklady, snížíme nl[[]]náklady]] a naučíme vás dělat nl[[]]logistiku správně"
				/>
			</span>
		</h1>
		<div
			class="w-[19rem] xs:w-[25rem] sm:w-[35rem] md:w-[40rem] lg:w-[50rem] mx-auto bg-white/75 backdrop-blur-[8px] p-4 md:p-6 rounded-3xl"
		>
			<p class="text-2xs/[1.4] md:text-base/[1.4] tracking-[0.012em] text-center font-medium">
				<Formatted
					text="Tým Commarec vám hodí záchranný kruh se vším od logistického poradenství, přes dočasné řízení logistiky až po stěhování skladu. Nejen radíme a navrhujeme, ale sami jdeme do provozu a věci skutečně měníme."
				/>
			</p>
		</div>
	</section>

	<section class="my-8 md:mt-12 md:mb-16 lg:mt-16 lg:mb-20 lg:mx-4 xl:mx-0">
		<!-- Switch -->
		<div
			class="flex gap-2 mx-auto w-full md:w-fit border-[1.5px] border-cmcsecondary rounded-xl p-1 mb-8 md:mb-12 lg:mb-16"
		>
			{#each Object.values(SolutionCategorization) as categorization (categorization)}
				<button
					type="button"
					aria-pressed={activeTab === categorization}
					onclick={(): void => {
						activeTab = categorization;
					}}
					class={[
						"w-[16rem] h-8 rounded-lg px-3 font-semibold text-2xs md:text-base uppercase transition-all duration-200 tracking-[0.05em]",
						activeTab === categorization ? "bg-cmcsecondary text-white" : "text-cmcsecondary/90",
					]}
				>
					{solutionCategorizationLongNameMap[categorization][tenant.locale]}
				</button>
			{/each}
		</div>
		<div class="grid md:grid-cols-2 gap-2 lg:flex lg:flex-col">
			{#each itemsToRender as { id, icon, name, description, href } (id)}
				<a
					{href}
					class="flex flex-col gap-x-4 lg:h-52 lg:flex-row bg-white/50 backdrop-blur-[8px] hover:bg-white hover:backdrop-blur-none transition-all p-4 lg:p-6 rounded-xl md:rounded-3xl group/underline"
				>
					<div class="flex md:flex-col lg:flex-row items-center lg:flex-1 md:items-start mb-3 md:mb-6 lg:mb-0">
						<div class="flex md:flex-col lg:flex-row gap-2 md:gap-12 lg:gap-6 items-center md:items-start">
							{#if icon}
								<Icon {icon} class="size-8 md:size-16 lg:size-12 text-cmcprimary shrink-0" />
							{/if}

							<div
								class="uppercase font-[1000] text-2xl/[0.9] md:text-[2.5rem]/[0.9] lg:text-5xl/[0.9] tracking-[0.002em] text-cmcsecondary text-center md:text-left font-druk-cond line-clamp-3 py-[0.09em]"
							>
								<Formatted text={name} />
							</div>
						</div>
					</div>

					<div class="flex flex-col flex-1">
						<div class="min-w-0">
							<div
								class="line-clamp-[6] md:line-clamp-[5] lg:line-clamp-4 overflow-hidden text-2xs/[1.5] md:text-base font-medium tracking-[0.016em]"
							>
								<Formatted text={description} />
							</div>
						</div>

						<div class="gap-4 pt-4 md:pt-6 mt-auto">
							<Button
								type="span"
								variant={ButtonVariant.Text}
								icon="arrow"
								class="flex items-center justify-center gap-4"
							>
								<Formatted text="hover[[Zjistit více]]" />
							</Button>
						</div>
					</div>
				</a>
			{/each}
		</div>
	</section>
</Layout>
